/* eslint-disable camelcase */
import axios from 'axios';
import { splitLocaleIntoCountryAndLanguage } from '../utils/determineLocale';
import {
  COUNTRIES,
  HPX_SUPPORTED_LANGUAGES,
  LANGUAGES,
  PSA_SUPPORTED_LANGUAGES,
  SIMPLIFIED_CHINESE_COUNTRIES,
  SUPPORTED_LANGUAGES
} from '../constants/i18n';

import {
  DEFAULT_USER_CONSENTS,
  DEFAULT_PRINTER_CONSENTS,
  DEFAULT_USER_CONSENTS_EASY_START_PRO,
  DEFAULT_USER_CONSENTS_EASY_START,
  DEFAULT_WJA_CONSENTS,
  DEFAULT_WELCOME_CONSENTS_SURE_SUPPLY
} from '../constants/purposeID';

const instance = axios.create();

const base = '/api';

export async function consents(requestObj, version = 2) {
  const { hostname } = window.location;
  const { port } = window.location;

  const { url, method = 'POST', data = '', ...options } = requestObj;
  const apiBase = version === 2 ? base : '/in-app-api';
  const consentBase = `${apiBase}/${version}`;
  const fullUrl = `//${hostname.replace(/^www./, 'api.')}${
    port ? `:${port}` : ''
  }${consentBase}/consents${url}`;
  
  try {
    const response = await axios({
      url: fullUrl,
      method,
      withCredentials: true,
      data,
      ...options
    });
    return response;
  } catch (e) {
    e.method = method;
    e.url = fullUrl;
    throw e;
  }
}

export const getWelcomeData = async (id, purposeIdList, language, country) =>
  await consents({
    url: '',
    data: {
      language,
      country,
      purposeIdList,
      deviceId: id
    }
  });

export const postConsents = (id, purposeId, language, resourceId, country) => {
  const url = `?deviceId=${id}&purposeId=${purposeId}&language=${language}&country=${country}&resourceId=${resourceId}&state=OptedIn`;
  return consents({ url, method: 'PATCH' });
};

export const postSureSupplyConsents = (id, language, country, platform, data) => {
  const url = `?deviceId=${id}&language=${language}&country=${country}&platform=${platform}`;
  return consents({ url, method: 'PATCH', data });
};

export const getCompanyTenantIdConsents = async (id, locale) =>
  await consents({
    url: `/company-tenantid?companyTenantId=${id}&userLocale=${locale}`,
    method: 'GET'
  });

export const getCompanyTenantConsents = async (
  id,
  purposeIdList,
  language,
  country,
  companyTenantId
) =>
  await consents({
    url: '',
    data: {
      language,
      purposeIdList,
      deviceId: id,
      tenantId: companyTenantId
    }
  });

export const getUserPrinterConsents = async (id, purposeIdList, language) =>
  // const url = `?deviceId=${id}&language=${language}&country=${country}`;
  // return await consents({ url });
  await consents({
    url: '',
    data: {
      language,
      purposeIdList,
      deviceId: id
    }
  });

export const setCompanyTanentConsents = (id, language, country, platform, data) => {
  const url = `/wfh?tenantId=${id}&language=${language}&country=${country}&platform=${platform}`;
  return consents({ url, method: 'PATCH', data });
};

export const setUserPrinterConsents = (id, language, country, platform, data) => {
  const url = `?deviceId=${id}&language=${language}&country=${country}&platform=${platform}`;
  return consents({ url, method: 'PATCH', data });
};

export const getDefaultConsents = async (
  userOrPrinter,
  hasAppNameEasyStartPro,
  hasAppNameEasyStart,
  hasAppNameEasyStartModifyConsent,
  hasAppNameWJA,
  hasAppNameSureSupply
) => {
  let default_consents;

  if (hasAppNameEasyStartPro) {
    default_consents = DEFAULT_USER_CONSENTS_EASY_START_PRO;
  } else if (hasAppNameEasyStart) {
    default_consents = DEFAULT_USER_CONSENTS_EASY_START;
  } else if (hasAppNameEasyStartModifyConsent) {
    default_consents = DEFAULT_USER_CONSENTS_EASY_START;
  } else if (hasAppNameWJA) {
    default_consents = DEFAULT_WJA_CONSENTS;
  } else if (hasAppNameSureSupply) {
    default_consents = DEFAULT_WELCOME_CONSENTS_SURE_SUPPLY;
  } else {
    default_consents =
      userOrPrinter === 'printer' ? DEFAULT_PRINTER_CONSENTS : DEFAULT_USER_CONSENTS;
  }

  return default_consents;
};

const ensureSupportedDefaultLocale = (locale ,isHPX = false, isPSAConsentPage = false) => {
  const { language, country } = splitLocaleIntoCountryAndLanguage(locale);
  if (language === LANGUAGES.ZH) {
    if (SIMPLIFIED_CHINESE_COUNTRIES.has(country)) {
      return `${language}-${COUNTRIES.CN}`;
    }
    if (country === COUNTRIES.HK) {
      return `${language}-${COUNTRIES.TW}`;
    }
    return `${language}-${country}`;
  }
  const defaultCountry = (() => {
    if (isHPX && HPX_SUPPORTED_LANGUAGES[language]) {
      return HPX_SUPPORTED_LANGUAGES[language].includes(country)
        ? country
        : HPX_SUPPORTED_LANGUAGES[language][0];
    } else if (isPSAConsentPage && PSA_SUPPORTED_LANGUAGES[language]) {
      return PSA_SUPPORTED_LANGUAGES[language].includes(country)
        ? country
        : PSA_SUPPORTED_LANGUAGES[language][0];
    }
    return SUPPORTED_LANGUAGES[language]?.[0] || country;
  })();

  return `${language}-${defaultCountry}`;
};

export const getCraftData = (resource, locale) => {
  const craftLocale = ensureSupportedDefaultLocale(locale);
  return axios.get(`${base}/${resource}.json.${craftLocale}`);
};

export const getContentStackData = async (
  resource,
  locale,
  stack,
  isHPX = false,
  isPSAConsentPage = false
) => {
  const csLocale = ensureSupportedDefaultLocale(locale, isHPX, isPSAConsentPage);

  const contentTypeId = Object.keys(resource)[0];
  const entryId = Object.values(resource)[0];

  const fullUrl = `/in-app-api/1/contentstack/${contentTypeId}/${entryId}?locale=${encodeURIComponent(
    csLocale
  )}${stack ? `&stack=${encodeURIComponent(stack)}` : ''}`;

  try {
    const response = await instance.request({
      url: fullUrl,
      method: 'GET',
      withCredentials: true
    });
    return response;
  } catch (e) {
    return e;
  }
};

